import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, InputAdornment, TextField, Typography } from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SearchIcon from "@mui/icons-material/Search";
import { GridLoader } from "react-spinners";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  buscarSumarizacaoEcdApuracao,
  buscarLancamentosEcdApuracao,
} from "../../../services/apuracao.service";
import Pagination from "../../../components/Pagination";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import BreadcrumbApuracao from "../components/BreadcrumbApuracao";
import SelectFiltro, { FILTER_FIELD_SX } from "../components/SelectFiltro";
import { formatarCnpj, fmtMesAno, formatarDescricaoConta } from "../utils";
import { formatarMoedaBRL } from "../Detalhe/utils/calcularTotaisPorAno.util";
import styles from "../Detalhe/detalhe.styles.module.css";
import tableStyles from "../../../components/TableCard/TableView/styles.module.css";

const NATUREZA_OPCOES = [
  { value: "", label: "Todos" },
  { value: "D", label: "Débito" },
  { value: "C", label: "Crédito" },
];

function exportarCsv(linhas, nomeArquivo) {
  const cabecalho = [
    "data",
    "numero_lancamento",
    "historico",
    "natureza",
    "contrapartida",
    "valor",
  ].join(";");
  const corpo = linhas.map((l) =>
    [l.dataLancamento, l.numeroLancamento, l.historico ?? "", l.natureza, l.contrapartida ?? "", l.valor]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
      .join(";")
  );
  const blob = new Blob([[cabecalho, ...corpo].join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeArquivo;
  a.click();
  URL.revokeObjectURL(url);
}

export default function LancamentosEcd() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // TXM-598 — conta e competência NÃO são filtros interativos aqui: o card pede só "filtro
  // por natureza (D/C) e busca por nº/histórico" (protótipo apuracao_icms_ecd_lancamentos.html
  // não tem seletor de conta/competência). Ambos são fixados pela linha clicada na aba
  // (chegam via ?conta=/&comp= na URL), mostrados como texto no subtítulo; pra ver outra
  // conta/competência, o usuário volta e clica em "Detalhes" na linha desejada.
  const conta = searchParams.get("conta") || "";
  const competencia = searchParams.get("comp") || "";

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [apuracao, setApuracao] = useState(null);
  const [descricaoConta, setDescricaoConta] = useState("");
  const [natureza, setNatureza] = useState("");
  const [busca, setBusca] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [pagina, setPagina] = useState(1);
  const [total, setTotal] = useState(0);
  const [linhas, setLinhas] = useState([]);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    let ativo = true;
    async function carregarApuracao() {
      const res = await buscarSumarizacaoEcdApuracao(id);
      if (!ativo || !res.sucesso) return;
      setApuracao(res.apuracao || null);
      const linhaConta = (res.linhas || []).find((l) => l.codCta === conta);
      setDescricaoConta(linhaConta ? formatarDescricaoConta((linhaConta.descricaoConta || "").trim()) : "");
    }
    carregarApuracao();
    return () => {
      ativo = false;
    };
  }, [id, conta]);

  const carregarLancamentos = useCallback(async () => {
    if (!conta || !competencia) {
      setLoading(false);
      setErro("Selecione uma conta e uma competência.");
      return;
    }
    setLoading(true);
    setErro("");
    const res = await buscarLancamentosEcdApuracao(id, {
      competencia,
      conta,
      natureza: natureza || undefined,
      busca: busca.trim() || undefined,
      pagina,
      pageSize,
    });
    setLoading(false);
    if (!res.sucesso) {
      setErro(res.mensagem || "Não foi possível carregar os lançamentos.");
      return;
    }
    setLinhas(res.linhas);
    setTotal(res.total);
  }, [id, conta, competencia, natureza, busca, pagina, pageSize]);

  useEffect(() => {
    carregarLancamentos();
  }, [carregarLancamentos]);

  function limparFiltros() {
    setBusca("");
    setNatureza("");
    setPagina(1);
  }

  // TXM-598 — mesma correção de LancamentosEcf: esta tela usa paginação SERVER-SIDE, então
  // `linhas` é só a página atual. Exportar `linhas` direto perderia o resto do filtro
  // silenciosamente sempre que total > pageSize. Busca todas as páginas (pageSize máximo do
  // backend, 100) respeitando o filtro atual antes de gerar o CSV.
  async function handleExportar() {
    setExportando(true);
    try {
      const nomeArquivo = `apuracao_icms_ecd_lancamentos_${conta}_${competencia}.csv`;
      let acumulado = [];
      let paginaBusca = 1;
      const PAGE_SIZE_EXPORT = 100;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const res = await buscarLancamentosEcdApuracao(id, {
          competencia,
          conta,
          natureza: natureza || undefined,
          busca: busca.trim() || undefined,
          pagina: paginaBusca,
          pageSize: PAGE_SIZE_EXPORT,
        });
        if (!res.sucesso || !res.linhas?.length) break;
        acumulado = acumulado.concat(res.linhas);
        if (acumulado.length >= res.total || res.linhas.length < PAGE_SIZE_EXPORT) break;
        paginaBusca += 1;
      }
      exportarCsv(acumulado, nomeArquivo);
    } finally {
      setExportando(false);
    }
  }

  return (
    // navigate(-1) em vez de reconstruir a URL: `id` aqui é o id da apuração ECD (filha),
    // não o id do pai ICMS que /detalhe/icms espera — mesmo padrão de LancamentosNotasFiscais,
    // sem tela standalone "/detalhe/ecd" pra voltar.
    <BreadcrumbApuracao
      itens={[
        { label: "Apurações", onClick: () => navigate("/relatorios/apuracoes") },
        { label: `Apuração ICMS — ${apuracao?.empresa || "—"}`, onClick: () => navigate(-1) },
        { label: "Lançamentos ECD" },
      ]}
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <ApuracaoCardShell
        hideContainer
        innerCardSx={{ border: "none", margin: 0 }}
          conteudoComScrollProprio
          Icone={MenuBookIcon}
          titulo="Lançamentos Contábeis — Razão (SPED ECD)"
          subtitulo={
            <>
              {apuracao?.empresa || "—"} · {formatarCnpj(apuracao?.cnpj)}
              <br />
              Conta: {conta || "—"}
              {descricaoConta ? ` — ${descricaoConta}` : ""} · Competência:{" "}
              {competencia ? fmtMesAno(competencia) : "—"}
            </>
          }
          acaoHeader={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography sx={{ color: "#666", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                {total} lançamentos no filtro
              </Typography>
              <Button
                variant="contained"
                size="small"
                disabled={total === 0 || exportando}
                onClick={handleExportar}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#18316e", minHeight: 40, whiteSpace: "nowrap", px: 2, "&:hover": { bgcolor: "#152858" } }}
              >
                {exportando ? "Exportando…" : "Exportar CSV"}
              </Button>
            </Box>
          }
          filtros={
            // TXM-598 — só os filtros que o card pede pra esta tela (busca por nº/histórico +
            // natureza), mesma ordem do protótipo apuracao_icms_ecd_lancamentos.html.
            <>
              <TextField
                size="small"
                label="Nº lançamento ou histórico"
                placeholder="Pesquisar número ou histórico"
                value={busca}
                onChange={(e) => {
                  setBusca(e.target.value);
                  setPagina(1);
                }}
                sx={{ ...FILTER_FIELD_SX, width: 280 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#3c4043" }} fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <SelectFiltro
                label="Natureza do lançamento"
                value={natureza}
                onChange={(v) => {
                  setNatureza(v);
                  setPagina(1);
                }}
                options={NATUREZA_OPCOES}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={limparFiltros}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
              >
                Limpar filtros
              </Button>
            </>
          }
          paginacao={
            !loading &&
            linhas.length > 0 && (
              <Pagination
                paginationKey="lancamentos-ecd"
                serverSideTotal={total}
                serverSidePage={pagina}
                serverSideRowsPerPage={pageSize}
                onServerPageChange={(novaPagina) => setPagina(novaPagina)}
                onServerRowsPerPageChange={(novoPageSize) => {
                  setPageSize(novoPageSize);
                  setPagina(1);
                }}
                disabled={loading}
              />
            )
          }
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <GridLoader color="#1565c0" size={10} />
            </Box>
          ) : linhas.length === 0 && !erro ? (
            <p className={styles.emptyMsg}>Nenhum lançamento encontrado com os filtros informados.</p>
          ) : (
              <div style={{ margin: "8px 0 0", overflow: "auto" }}>
                <table className={tableStyles.accordion_table}>
                  <thead>
                    <tr>
                      <th scope="col">Data</th>
                      <th scope="col">Nº lançamento</th>
                      <th scope="col">Histórico</th>
                      <th scope="col">D/C</th>
                      <th scope="col">Contrapartida</th>
                      <th scope="col" style={{ textAlign: "right" }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((l, i) => (
                      <tr key={`${l.idRegistro}-${i}`}>
                        <td>{l.dataLancamento || "—"}</td>
                        <td style={{ fontFamily: "ui-monospace, monospace" }}>{l.numeroLancamento || "—"}</td>
                        <td>{l.historico || "—"}</td>
                        <td>
                          <span
                            className={styles.badgeTipo}
                            style={
                              l.natureza === "D"
                                ? { background: "#e3ecf7", color: "#1e4a7a" }
                                : { background: "#fdecd8", color: "#ad4e00" }
                            }
                          >
                            {l.natureza === "D" ? "Débito" : "Crédito"}
                          </span>
                        </td>
                        <td>{l.contrapartida || "—"}</td>
                        <td style={{ textAlign: "right", fontFamily: "ui-monospace, monospace" }}>{formatarMoedaBRL(l.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          )}
      </ApuracaoCardShell>
    </BreadcrumbApuracao>
  );
}
